import React, { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { Modal, Icon, Spinner, Empty, useToast } from "../components/ui";

const AUDIENCES = [
  ["ALL", "Everyone"],
  ["USERS", "Users only"],
  ["ADMINS", "Admins & supervisors"],
];
const audLabel = (a) => (AUDIENCES.find(([k]) => k === a) || [a, a])[1];

export function FaqManager({ onClose }) {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);   // {} for new, {id,...} for edit
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => { api.listFaqs().then(setRows).catch((e) => { toast(e.message, "err"); setRows([]); }); }, [toast]);
  useEffect(() => { load(); }, [load]);

  const togglePublish = async (f) => {
    try { await api.setFaqPublished(f.id, !f.published); load(); }
    catch (e) { toast(e.message, "err"); }
  };
  const del = async (id) => {
    setConfirm(null);
    try { await api.deleteFaq(id); toast("FAQ deleted", "ok"); load(); }
    catch (e) { toast(e.message, "err"); }
  };

  return (
    <Modal wide title="FAQ & help content" sub="Write and publish help answers for each user level" onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Close</button>
        <button className="btn btn-primary" onClick={() => setEditing({})}><Icon.plus width="15" height="15" /> New FAQ</button></>}>
      {rows === null ? <div className="empty"><Spinner /></div>
        : rows.length === 0 ? <Empty icon={<Icon.help width="40" height="40" />} title="No FAQs yet" text="Create your first help answer with “New FAQ”." />
        : <div className="faq-mgr-list">
            {rows.map((f) => (
              <div className="faq-mgr-item" key={f.id}>
                <div className="faq-mgr-main">
                  <div className="faq-mgr-q">{f.question}</div>
                  <div className="faq-mgr-meta">
                    <span className={"aud-pill aud-" + f.audience}>{audLabel(f.audience)}</span>
                    {f.category && <span className="faq-mgr-cat">{f.category}</span>}
                    <span className={"pub-pill" + (f.published ? " on" : "")}>{f.published ? "Published" : "Draft"}</span>
                  </div>
                </div>
                <div className="faq-mgr-actions">
                  <button className="link-btn" onClick={() => togglePublish(f)}>{f.published ? "Unpublish" : "Publish"}</button>
                  <button className="link-btn" onClick={() => setEditing(f)}>Edit</button>
                  <button className="link-btn danger" onClick={() => setConfirm(f)}>Delete</button>
                </div>
              </div>
            ))}
          </div>}

      {editing && <FaqEditor faq={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {confirm && (
        <Modal title="Delete FAQ" onClose={() => setConfirm(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => del(confirm.id)}>Delete</button></>}>
          <p style={{ margin: 0 }}>Delete “{confirm.question}”? This can't be undone.</p>
        </Modal>
      )}
    </Modal>
  );
}

function FaqEditor({ faq, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !faq.id;
  const [question, setQuestion] = useState(faq.question || "");
  const [answer, setAnswer] = useState(faq.answer || "");
  const [category, setCategory] = useState(faq.category || "");
  const [audience, setAudience] = useState(faq.audience || "ALL");
  const [published, setPublished] = useState(faq.published ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    if (!question.trim()) return setErr("Question is required.");
    if (!answer.trim()) return setErr("Answer is required.");
    setBusy(true);
    try {
      await api.saveFaq(faq.id, { question: question.trim(), answer: answer.trim(), category: category.trim() || null, audience, published });
      toast(isNew ? "FAQ created" : "FAQ updated", "ok"); onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal wide title={isNew ? "New FAQ" : "Edit FAQ"} onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? <span className="spin" /> : "Save"}</button></>}>
      <div className="field"><label>Question</label>
        <input className="inp" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="e.g. How do I request an archive?" /></div>
      <div className="field"><label>Answer</label>
        <textarea className="inp" rows={5} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="Write the answer…" /></div>
      <div className="row-2">
        <div className="field"><label>Show to (user level)</label>
          <select className="inp" value={audience} onChange={(e) => setAudience(e.target.value)}>
            {AUDIENCES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select></div>
        <div className="field"><label>Category (optional)</label>
          <input className="inp" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Getting started" /></div>
      </div>
      <label className="chk-line"><input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} /> Publish now (visible to the selected user level)</label>
      {err && <div className="err-text">{err}</div>}
    </Modal>
  );
}